/**
 * NewContractPage · v2.7.35 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Primary onboarding form for Enterprise Contracts. Auto-generates the
 * ID: BTL-[CLASS-CODE]-[YEAR]-[5-digit serial] on submit.
 *
 * Visible to: super_user (admin/developer), partner (operating_partner),
 * supervisor, dispatch — but write actions enforced server-side and
 * supervisor/dispatch will see "Insufficient Administrative Privileges".
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { contractsApi, CLASSIFICATION_LABELS, CLASSIFICATION_TO_URL_SLUG } from "@/lib/contractsApi";

export default function NewContractPage() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    classification: "business",
    business_name: "",
    business_website: "",
    business_address: "",
    contact_first_name: "",
    contact_last_name: "",
    primary_office_phone: "",
    mobile_phone: "",
    notes: "",
    rate_multiplier: 1.0,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.business_name.trim() || !form.contact_first_name.trim() || !form.contact_last_name.trim()) {
      toast.error("Business Name, Contact First & Last name are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await contractsApi.create({
        ...form,
        rate_multiplier: parseFloat(form.rate_multiplier) || 1.0,
      });
      toast.success(`Contract created · ${data.id}`);
      const slug = CLASSIFICATION_TO_URL_SLUG[data.classification] || data.classification;
      nav(`/admin/contracts/${slug}/${data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Create failed";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="new-contract-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4 flex-wrap">
          <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider" data-testid="back-to-admin">
            <ArrowLeft className="h-4 w-4"/>Admin Console
          </Link>
          <div className="h-6 w-px bg-zinc-800"/>
          <Briefcase className="h-6 w-6 text-emerald-300"/>
          <h1 className="text-2xl font-extrabold tracking-tight">New Contract</h1>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
            ID auto-generated · BTL-[CLASS]-{new Date().getFullYear()}-XXXXX
          </span>
        </div>

        <Card className="bg-zinc-900 border-zinc-800 p-6 space-y-5">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Contract Classification *</Label>
            <Select value={form.classification} onValueChange={set("classification")}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 mt-1.5" data-testid="contract-classification-select">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700">
                {Object.entries(CLASSIFICATION_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k} data-testid={`contract-classification-${k}`}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Business Name *" testid="contract-business-name">
              <Input value={form.business_name} onChange={set("business_name")} className="bg-zinc-950 border-zinc-700" data-testid="contract-business-name-input"/>
            </Field>
            <Field label="Business Website" testid="contract-business-website">
              <Input value={form.business_website} onChange={set("business_website")} className="bg-zinc-950 border-zinc-700" placeholder="https://" data-testid="contract-business-website-input"/>
            </Field>
            <Field label="Business Address" testid="contract-business-address" full>
              <Input value={form.business_address} onChange={set("business_address")} className="bg-zinc-950 border-zinc-700" data-testid="contract-business-address-input"/>
            </Field>
            <Field label="Contact First Name *" testid="contract-contact-first">
              <Input value={form.contact_first_name} onChange={set("contact_first_name")} className="bg-zinc-950 border-zinc-700" data-testid="contract-contact-first-input"/>
            </Field>
            <Field label="Contact Last Name *" testid="contract-contact-last">
              <Input value={form.contact_last_name} onChange={set("contact_last_name")} className="bg-zinc-950 border-zinc-700" data-testid="contract-contact-last-input"/>
            </Field>
            <Field label="Primary Office Phone" testid="contract-office-phone">
              <Input value={form.primary_office_phone} onChange={set("primary_office_phone")} className="bg-zinc-950 border-zinc-700" placeholder="+1 405 555 0100" data-testid="contract-office-phone-input"/>
            </Field>
            <Field label="Mobile Phone" testid="contract-mobile-phone">
              <Input value={form.mobile_phone} onChange={set("mobile_phone")} className="bg-zinc-950 border-zinc-700" placeholder="+1 405 555 0200" data-testid="contract-mobile-phone-input"/>
            </Field>
            <Field label="Commercial Rate Multiplier" testid="contract-rate-mult">
              <Input type="number" step="0.05" min="0" max="5" value={form.rate_multiplier} onChange={set("rate_multiplier")} className="bg-zinc-950 border-zinc-700" data-testid="contract-rate-mult-input"/>
              <p className="text-[10px] text-zinc-500 mt-1">1.0 = standard · 0.9 = 10 % discount · 1.2 = 20 % surcharge. Sub-accounts inherit this baseline.</p>
            </Field>
          </div>

          <Field label="Notes" testid="contract-notes">
            <Textarea value={form.notes} onChange={set("notes")} rows={3} className="bg-zinc-950 border-zinc-700" data-testid="contract-notes-input"/>
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" className="text-zinc-300"><Link to="/admin">Cancel</Link></Button>
            <Button onClick={submit} disabled={busy} className="bg-emerald-500 text-zinc-900 hover:bg-emerald-400 font-bold" data-testid="contract-create-btn">
              <Save className="h-4 w-4 mr-2"/>{busy ? "Creating…" : "Create Contract"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children, testid, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
